import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, Input, Button, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FIREBASE_REGION } from '~/services/firebase';
import { useTranslation } from 'react-i18next';
import * as styles from './AcceptInvitation.module.scss';

const { Title, Text } = Typography;

interface AcceptInvitationForm {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const AcceptInvitation = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [form] = Form.useForm<AcceptInvitationForm>();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Extract token from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const handleSubmit = async (values: AcceptInvitationForm) => {
    if (!token) {
      message.error(t<string>('acceptInvitation.invalidLink'));

      return;
    }

    setLoading(true);

    try {
      const functions = getFunctions(undefined, FIREBASE_REGION);
      const acceptInvitation = httpsCallable(functions, 'team-acceptInvitation');
      await acceptInvitation({
        token,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      });

      message.success(t<string>('acceptInvitation.successMessage'));
      setTimeout(() => {
        history.push('/auth/login');
      }, 1500);
    } catch (error) {
      const errorMessage = (error as Error)?.message || t<string>('acceptInvitation.failedMessage');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.header}>
            <Title level={3} className={styles.headerTitle}>
              {t<string>('acceptInvitation.invalidTitle')}
            </Title>
            <Text type="secondary">{t<string>('acceptInvitation.invalidDescription')}</Text>
          </div>
          <Button type="primary" block onClick={() => history.push('/auth/login')}>
            {t<string>('acceptInvitation.goToSignIn')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <Title level={3} className={styles.headerTitle}>
            {t<string>('acceptInvitation.title')}
          </Title>
          <Text type="secondary" className={styles.headerSubtitle}>
            {t<string>('acceptInvitation.subtitle')}
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t<string>('firstName')}
            name="firstName"
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
            ]}
          >
            <Input prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item
            label={t<string>('lastName')}
            name="lastName"
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
            ]}
          >
            <Input prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item
            label={t<string>('password')}
            name="password"
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
              {
                min: 8,
                message: t<string>('common:validationErrors.minLength', { number: 8 }),
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t<string>('acceptInvitation.passwordPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t<string>('acceptInvitation.confirmPassword')}
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: t<string>('common:validationErrors.fieldIsRequired'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(t<string>('common:validationErrors.passwordNotMatch'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t<string>('acceptInvitation.confirmPasswordPlaceholder')}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block loading={loading} size="large">
              {t<string>('acceptInvitation.createAccount')}
            </Button>
          </Form.Item>
        </Form>

        <div className={styles.footer}>
          <Text type="secondary">
            {t<string>('alreadyHaveAccount')}{' '}
            <Button type="link" onClick={() => history.push('/auth/login')} style={{ padding: 0 }}>
              {t<string>('signIn')}
            </Button>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
